<template>
  <div class="customer-tab">
    <div class="columns is-multiline">
      <div
        v-for="counter in counters"
        :key="counter.label"
        class="column is-one-quarter"
      >
        <div class="box has-text-centered">
          <p class="heading">{{ counter.label }}</p>
          <p class="title is-4">{{ counter.value }}</p>
        </div>
      </div>
    </div>
    <div class="notification is-info is-light">
      <strong>Ακύρωση σύστασης</strong>
      <p>
        Η ακύρωση σταματά οριστικά τη σύσταση πριν από την ανταμοιβή. Κανείς δεν
        λαμβάνει τους πόντους σύστασης, ακόμη κι αν ο φίλος κάνει αργότερα
        qualifying παραγγελία. Ο φίλος κρατά το κουπόνι που έλαβε στην εγγραφή,
        δεν αποστέλλεται email και η ενέργεια είναι μη αναστρέψιμη.
      </p>
    </div>
    <table
      v-if="referrals.length"
      class="table is-bordered is-striped is-fullwidth"
    >
      <thead>
        <tr>
          <th>Φίλος</th>
          <th>Εγγραφή</th>
          <th>Κατάσταση</th>
          <th>Αξία προϊόντων μετά τις εκπτώσεις</th>
          <th>Πόντοι φίλου / πελάτη</th>
          <th />
        </tr>
      </thead>
      <tbody>
        <tr v-for="referral in referrals" :key="referral.uuid || referral.id">
          <td>
            {{ friendName(referral) }}<br /><small>{{
              friendEmail(referral)
            }}</small>
          </td>
          <td>{{ referral.registered_at | timestamp }}</td>
          <td>
            <span :class="['tag', statusClass(referral.status)]">{{
              statusLabel(referral.status)
            }}</span>
          </td>
          <td v-if="referral.qualifying_order">
            <router-link
              :to="{
                name: 'orders.edit',
                params: { uuid: referral.qualifying_order.uuid },
              }"
            >
              {{ referral.qualifying_order.common_id }}
            </router-link>
            · {{ referral.qualifying_order.discounted_products_total }}€<br />{{
              referral.qualifying_order.created_at | timestamp
            }}
          </td>
          <td v-else>—</td>
          <td>{{ pointValues(referral) }}</td>
          <td>
            <button
              v-if="referral.status === 'registered'"
              class="button is-small is-danger"
              @click="askCancel(referral)"
            >
              Ακύρωση
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <p v-else class="has-text-centered">Δεν υπάρχουν συστάσεις.</p>
    <Confirmation
      :is-open="isCancelOpen"
      :is-saving="isSaving"
      title="Επιβεβαίωση ακύρωσης"
      description="Η ακύρωση είναι οριστική και μη αναστρέψιμη."
      @closeModal="confirmCancel"
    />
  </div>
</template>

<script>
import request from '@/utils/request';
import Confirmation from '@/views/components/Confirmation';

export default {
  components: { Confirmation },
  props: { customer: Object },
  data: () => ({ pendingCancel: null, isCancelOpen: false, isSaving: false }),
  computed: {
    referrals() {
      const profile = this.customer && this.customer.profile;
      return (profile && profile.referrals_made) || [];
    },
    referredBy() {
      const profile = this.customer && this.customer.profile;
      return profile && profile.referred_by;
    },
    counters() {
      const rewarded = this.referrals.filter(
        referral => referral.status === 'rewarded',
      );
      return [
        { label: 'Συστάσεις', value: this.referrals.length },
        { label: 'Ολοκληρωμένες', value: rewarded.length },
        {
          label: 'Πόντοι από συστάσεις',
          value: rewarded.reduce(
            (sum, referral) => sum + this.pointsTotal(referral),
            0,
          ),
        },
        {
          label: 'Συστάθηκε από',
          value:
            (this.referredBy &&
              this.referredBy.referrer &&
              this.referredBy.referrer.firstName) ||
            '—, απευθείας εγγραφή',
        },
      ];
    },
  },
  methods: {
    friendName(item) {
      return (
        (item.referred &&
          (item.referred.firstName ||
            (item.referred.customer && item.referred.customer.fullName))) ||
        '••••'
      );
    },
    friendEmail(item) {
      return (item.referred && item.referred.email) || '••••';
    },
    statusLabel(status) {
      if (status === 'rewarded') return 'Ολοκληρώθηκε';
      if (status === 'cancelled') return 'Ακυρώθηκε';
      return 'Αναμονή';
    },
    statusClass(status) {
      if (status === 'rewarded') return 'is-success';
      if (status === 'cancelled') return 'is-danger';
      return 'is-warning';
    },
    pointsTotal(item) {
      return (item.points || []).reduce(
        (sum, point) => sum + Number(point.count || 0),
        0,
      );
    },
    pointValues(item) {
      if (item.status === 'registered')
        return 'αναμονή / αναμονή · κουπόνι φίλου: εκδόθηκε';
      const points = item.points || [];
      const friendPoints = points
        .filter(point => point.profile_id === item.referred_profile_id)
        .reduce((sum, point) => sum + Number(point.count || 0), 0);
      const customerPoints = points
        .filter(point => point.profile_id === item.referrer_profile_id)
        .reduce((sum, point) => sum + Number(point.count || 0), 0);
      return `${friendPoints} / ${customerPoints}`;
    },
    askCancel(referral) {
      this.pendingCancel = referral;
      this.isCancelOpen = true;
    },
    async confirmCancel(confirmed) {
      this.isCancelOpen = false;
      if (!confirmed || !this.pendingCancel) return;
      this.isSaving = true;
      try {
        await request.put(`/referrals/${this.pendingCancel.uuid}/cancel`);
        this.$set(this.pendingCancel, 'status', 'cancelled');
        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η σύσταση ακυρώθηκε.',
        });
      } catch (error) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'Η ακύρωση της σύστασης απέτυχε.',
        });
      } finally {
        this.isSaving = false;
        this.pendingCancel = null;
      }
    },
  },
};
</script>
